import React, {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Path, TableViewerProps, TableViewerRef, Value } from "./dto";
import {
  generateDataSourceByFormulas,
  generateFormValues,
  generateRenderConfig,
  getCellSize,
  getFieldNamePath,
  getFieldPath,
  getKeyPaths,
  getStyleByCell,
  isEditable,
  renderCell,
  renderCellWithEditable,
  transformFormValues,
} from "./utils";
import { cn } from "../utils";
import { Button, Form, Tooltip } from "@gw/web-basic-components";
import LoopAction, { LoopActionConfig } from "./plugins/loop-action";

const Viewer = forwardRef(
  (props: TableViewerProps, ref: Ref<TableViewerRef>) => {
    const {
      dataSource: initialDataSource = [],
      config,
      className,
      style,
      editable,
      errors = [],
      onValueChange,
      onChange,
      ...rest
    } = props;
    // 表单
    const [form] = Form.useForm();
    // 内部必填项校验
    const [innerErrors, setInnerErrors] = useState<TableViewerProps["errors"]>(
      []
    );
    // 数据源
    const [dataSource, setDataSource] = useState<Value[]>([]);
    // 循环操作配置
    const [loopActionsConfig, setLoopActionsConfig] =
      useState<LoopActionConfig>({ add: [], del: [] });
    // 表格引用
    const tableRef = useRef<HTMLTableElement>();
    // 表单初始值
    const initialValues = useMemo(
      () => generateFormValues(initialDataSource, config),
      [JSON.stringify(config), JSON.stringify(initialDataSource)]
    );
    // 带公式的数据源
    const withFormulasInitialDataSource = useMemo(
      () => generateDataSourceByFormulas(dataSource, config.formulas),
      [dataSource, JSON.stringify(config.formulas)]
    );
    // 渲染配置
    const renderConfig = useMemo(
      () => generateRenderConfig(config, withFormulasInitialDataSource),
      [JSON.stringify(config), withFormulasInitialDataSource]
    );
    // 重置数据源
    useEffect(() => {
      setDataSource(initialDataSource);
    }, [JSON.stringify(initialDataSource)]);
    // 重置表单
    useEffect(() => {
      form.resetFields();
    }, [initialValues]);
    // 向外部暴露的方法
    useImperativeHandle(ref, () => {
      return {
        getFieldValue: form.getFieldValue,
        getFieldsValue: () =>
          transformFormValues(
            form.getFieldsValue(),
            withFormulasInitialDataSource
          ),
        setFieldValue: form.setFieldValue,
        setFieldsValue: form.setFieldsValue,
        setFormValues: (v) => {
          form.setFieldsValue(generateFormValues(v, config));
        },
        resetFields: form.resetFields,
        reset: () => {
          setDataSource(initialDataSource);
          setInnerErrors([]);
          form.resetFields();
        },
        validateFields: () =>
          form
            .validateFields()
            .then((values) => {
              setInnerErrors([]);
              return transformFormValues(values, withFormulasInitialDataSource);
            })
            .catch((e) => {
              setInnerErrors(
                e.errorFields.map((i) => ({
                  path: i.name,
                  message: "请输入",
                }))
              );
              return Promise.reject(e);
            }),
        table: tableRef.current,
      };
    });
    // 表单值变更回调
    const onChangeWithFormValues = (path?: Path) => {
      const values = form.getFieldsValue();
      const fieldValues = path ? form.getFieldValue(path) : undefined;
      form
        .validateFields()
        .then(() => {
          setInnerErrors([]);
        })
        .catch((e) => {
          setInnerErrors(
            e.errorFields.map((i) => ({
              path: i.name,
              message: "请输入",
            }))
          );
        });
      onChange?.(
        path,
        fieldValues,
        transformFormValues(values, withFormulasInitialDataSource)
      );
    };
    // 渲染表格
    const renderBody = () => {
      return renderConfig.rows.map((row) => {
        const loopAttr = row.loop
          ? { "data-loop-id": row.loop?.id, "data-loop-index": row.loop?.index }
          : {};
        return (
          <tr key={row.id} data-id={row.id} {...loopAttr}>
            {row.cells.map((cell) => {
              const Tag = cell.tag ?? "td";
              const cellEditable = isEditable(
                cell,
                withFormulasInitialDataSource,
                config.formulas ?? []
              );
              const namePath = getFieldNamePath(cell).map((i) =>
                row.loop ? `${i}.${row.loop?.index}` : i
              );
              const nameAttr =
                namePath.length > 0
                  ? { ["data-name"]: namePath.join("_") }
                  : {};
              const error = [...innerErrors, ...errors].find((i) =>
                namePath.includes(
                  Array.isArray(i.path) ? i.path.join(".") : i.path
                )
              );
              const path = getFieldPath(
                cell?.value,
                dataSource,
                row?.loop?.index
              );
              const child = (
                <Tag
                  key={cell.id}
                  data-id={cell.id}
                  {...nameAttr}
                  rowSpan={cell.rowSpan}
                  colSpan={cell.colSpan}
                  style={{
                    ...getStyleByCell(cell.style),
                    ...getCellSize(cell, renderConfig),
                  }}
                  className={cn({
                    "wbuc-settlement-cell-editable": cellEditable && editable,
                    "wbuc-settlement-cell-error": !!error && editable,
                  })}
                >
                  {editable
                    ? renderCellWithEditable({
                        cell,
                        dataSource: withFormulasInitialDataSource,
                        editable: cellEditable,
                        index: row?.loop?.index,
                        onChange: onChangeWithFormValues,
                      })
                    : renderCell(
                        cell,
                        withFormulasInitialDataSource,
                        row?.loop?.index
                      )}
                </Tag>
              );
              if (editable && error) {
                return (
                  <Tooltip
                    key={cell.id}
                    title={
                      <div className="after:wbuc-clear-both after:wbuc-block after:wbuc-content-['']">
                        {error?.message}
                        <Button
                          className="wbuc-float-right"
                          size="small"
                          onClick={() => {
                            form.setFieldValue(path, error?.value);
                            onChangeWithFormValues(path);
                          }}
                          type="primary"
                        >
                          一键填充
                        </Button>
                      </div>
                    }
                  >
                    {child}
                  </Tooltip>
                );
              }
              return child;
            })}
          </tr>
        );
      });
    };
    // 渲染list字段（因为循环字段可以支持删除，如果全部删除则form会收集不到，所以需要额外渲染）
    const renderListFields = () => {
      const listFields = withFormulasInitialDataSource.filter((i) => i.isList);
      return listFields.map((i) => (
        <Form.Item noStyle name={i.code} key={i.code} />
      ));
    };
    return (
      <Form
        {...rest}
        form={form}
        initialValues={initialValues}
        onValuesChange={(currentValue, allValue) => {
          onValueChange?.(
            getKeyPaths(currentValue)[0],
            currentValue,
            transformFormValues(allValue, withFormulasInitialDataSource)
          );
        }}
        className={cn("wbuc-relative wbuc-w-fit wbuc-min-w-full", className, {
          "wbuc-pl-[4.5rem]": editable && loopActionsConfig.add.length > 0,
          "wbuc-pr-[4.5rem]": editable && loopActionsConfig.del.length > 0,
        })}
        style={style}
      >
        {renderListFields()}
        <table className="wbuc-settlement-viewer" ref={tableRef}>
          <tbody>{renderBody()}</tbody>
        </table>
        <LoopAction
          enabled={editable}
          dataSource={dataSource}
          tableConfig={config}
          renderConfig={renderConfig}
          getContainer={() => tableRef.current}
          onChange={setLoopActionsConfig}
          onDataSourceChange={(type, set) => {
            setDataSource((prev) =>
              set(prev, (code, value) => {
                if (type === "add") {
                  const originFormValues = form.getFieldValue(code) ?? [];
                  form.setFieldValue(code, [value, ...originFormValues]);
                }
                if (type === "remove") {
                  const originFormValues = [...form.getFieldValue(code)];
                  originFormValues.splice(value, 1);
                  form.setFieldValue(code, originFormValues);
                }
              })
            );
            setTimeout(onChangeWithFormValues);
          }}
        />
      </Form>
    );
  }
);

export default Viewer;
